// Sidebar.tsx
import type { ComponentType, SVGProps } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Folder,
  Database,
  Settings,
  Users,
  Camera,
  Key as KeyIcon,
  User as UserIcon,
  Sliders,
  Shield,
  Trash2,
  HardDrive,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { useAppStore, type Project } from "../stores/useAppStore";
import { useUser } from "../lib/auth/useSession";
import { useLogout } from "../lib/auth/useLogout";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import StorageStatusIndicator from "./StorageStatusIndicator";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

interface SidebarProps {
  projects?: Project[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

type NavChild = {
  id: string;
  name: string;
  icon: Icon;
  href: string;
};

type NavItem = {
  id: string;
  name: string;
  icon: Icon;
  href: string;
  children?: NavChild[];
};

function NavItemComponent({
  item,
  pathname,
}: {
  item: NavItem;
  pathname: string;
}) {
  const isActiveRoot = pathname === item.href;
  const isProjectsSection = item.id === 'projects';
  
  return (
    <>
      <Link
        to={item.href}
        prefetch="intent"
        className={`group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
          isActiveRoot
            ? "bg-primary/15 text-primary shadow-sm"
            : "text-foreground/70 hover:bg-accent hover:text-foreground hover:shadow-sm"
        }`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <item.icon className={`w-5 h-5 mr-3 transition-transform duration-200 ${
          isActiveRoot ? "" : "group-hover:scale-110"
        }`} />
        <span className="flex-1">{item.name}</span>
        {item.children && item.children.length > 0 && (
          <span className="text-xs font-semibold text-muted-foreground bg-muted rounded-full px-2.5 py-1">
            {item.children.length}
          </span>
        )}
      </Link>

      {item.children && item.children.length > 0 && (
        <div className={`ml-8 space-y-1.5 mt-2 overflow-y-auto min-h-0 ${
          isProjectsSection ? 'flex-1' : 'max-h-48'
        }`}>
          {item.children.map((child) => {
            const isActive = pathname.startsWith(child.href);
            return (
              <Link
                key={child.id}
                to={child.href}
                prefetch="intent"
                className={`group flex w-full items-center px-4 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground font-medium shadow-sm"
                    : "text-foreground/60 hover:bg-accent hover:text-foreground hover:shadow-sm"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <child.icon className={`w-4 h-4 mr-3 flex-shrink-0 transition-transform duration-200 ${
                  isActive ? "" : "group-hover:scale-110"
                }`} />
                <span className="truncate text-left">{child.name}</span>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function Sidebar({
  projects = [],
  collapsed = false,
  onToggleCollapse,
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { uploadAvatar } = useAppStore();
  const user = useUser();
  const logout = useLogout();

  // Extract current project from path (kept for possible usage)
  const _currentProject = location.pathname.split("/")[1];

  const navigation: NavItem[] = [
    {
      id: "projects",
      name: "Projects",
      icon: Folder,
      href: "/projects",
      children: [...projects]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((project) => ({
          id: `project-${project.id}`,
          name: project.name,
          icon: Database,
          href: `/projects/${project.id}/buckets`,
        })),
    },
    { id: "storage", name: "Storage", icon: HardDrive, href: "/storage" },
    { id: "trash", name: "Trash", icon: Trash2, href: "/trash" },
    { id: "users", name: "Users", icon: Users, href: "/users" },
  ];

  const settingsDropdown: NavItem = {
    id: "settings",
    name: "Settings",
    icon: Settings,
    href: "/settings",
    children: [
      { id: "settings-api-keys", name: "API Keys", icon: KeyIcon, href: "/settings?tab=api-keys" },
      { id: "settings-preferences", name: "Security & 2FA", icon: Shield, href: "/settings?tab=preferences" },
    ],
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div
      className={`flex flex-col h-screen hidden lg:flex transition-all duration-300 ease-in-out relative overflow-hidden ${
        collapsed ? 'w-0 border-r-0' : 'w-80'
      }`}
      style={{ backgroundColor: '#F9FAFB' }}
    >
      {/* Floating toggle button - positioned based on collapsed state */}
      <button
        onClick={onToggleCollapse}
        className={`fixed top-1/2 -translate-y-1/2 z-50 bg-card border border-border shadow-md hover:bg-accent transition-all duration-200 hover:shadow-lg cursor-pointer ${
          collapsed
            ? 'left-0 rounded-r-lg p-2'
            : 'left-[calc(320px-12px)] rounded-full p-1.5'
        }`}
        style={collapsed ? {} : { transform: 'translateY(-50%)' }}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {/* Header */}
      <div className="p-6 border-b border-border/40">
        <Link to="/projects" className="flex items-center space-x-3 group">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-105">
            <Database className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-semibold text-xl text-foreground">Stash</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 flex flex-col min-h-0 overflow-hidden">
        {navigation.map((item) => (
          <div key={item.id} className={item.id === 'projects' ? 'flex-1 flex flex-col min-h-0 mb-2' : 'mb-1'}>
            <NavItemComponent
              key={item.id}
              item={item}
              pathname={location.pathname}
            />
          </div>
        ))}
      </nav>

      {/* Storage Status */}
      <div className="px-4 pb-2">
        <StorageStatusIndicator collapsed={collapsed} />
      </div>

      {/* Bottom Navigation */}
      <div className="px-4 py-5 border-t border-border/40 space-y-2">
        {/* Settings dropdown */}
        <NavItemComponent item={settingsDropdown} pathname={location.pathname} />

        {/* User Profile */}
        <div className="flex items-center px-4 py-4 mt-3 border-t border-border/40">
          <div className="relative group flex-shrink-0">
            <Avatar className="w-10 h-10 ring-2 ring-border/50 transition-all duration-200 group-hover:ring-primary/30">
              <AvatarImage src={user?.avatar_url} alt={user?.email} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-medium text-sm">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <button
              onClick={() => document.getElementById('avatar-upload-sidebar')?.click()}
              className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
            <input
              id="avatar-upload-sidebar"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file && uploadAvatar) {
                  try {
                    await uploadAvatar(file);
                  } catch (error) {
                    console.error('Avatar upload failed:', error);
                  }
                }
              }}
            />
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {user?.email?.split('@')[0] || 'User'}
            </p>
            <p className="text-xs text-muted-foreground truncate">{user?.email || 'user@stash.local'}</p>
          </div>
        </div>

        {/* Logout Button */}
        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full justify-start text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-all duration-200 h-11 cursor-pointer"
        >
          <LogOut className="w-4 h-4 mr-3" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}
